import { getTierMaterial, type BuildTier } from '../engine/materials';
import { SK } from './skin';

// Shared radial-wheel rendering + interaction, factored out of the build wheel so the hammer wheel
// can reuse it. A Rust-style RADIAL WHEEL: hold RIGHT MOUSE to open, move the mouse to hover a
// segment, release to select (clicking a segment also selects). A ring of segments surrounds a hub that
// shows the hovered label; optional tier swatches orbit just outside the ring and are hovered/clicked
// the same way.
//
// Both wheels keep the same lifecycle: show() / hide() open and close the overlay; commitSelection()
// applies whatever is hovered. Subclasses fill in the segment list, the swatch list, and what to do
// when a segment / swatch is committed.

export const SVG_NS = 'http://www.w3.org/2000/svg';

// Geometry of the dial, in SVG user units (the viewBox is square and centered on the hub).
export const VIEW = 520;
export const CENTER = VIEW / 2;
const R_INNER = 92; // hub radius (label lives here) / inner edge of the outer ring
const R_HUB_SMALL = 56; // shrunken hub when a wheel has an INNER RING (build wheel: material tiers)
const R_OUTER = 196; // segment-ring outer radius
const R_TIER = 232; // swatches orbit just outside the ring
const TIER_SW = 52; // swatch box size

// Style-guide wheel palette (ui-style-guide.md "Radial wheel spec"): glass slices whose gap strokes
// read as the page void, accent-soft fill + accent ring on hover/selection, opaque bg-1 hub disc.
const SEG_FILL = SK.glass;             // idle slice
const SEG_FILL_HOVER = SK.accentSoft;  // hovered slice (orange = interactive)
const SEG_FILL_ACTIVE = SK.accentSoft; // selected slice: same soft fill, marked by the accent ring
const SEG_STROKE = SK.bg0;             // slice gaps
const SEG_STROKE_ACTIVE = SK.accent;   // selection ring
const RED_STROKE = SK.accent;          // border on a hovered tier slice / swatch (pattern fills can't lift)
const EDGE_STROKE = SK.border;         // hub ring + swatch hairline
const TEXT_IDLE = SK.text;
const TEXT_HOVER = SK.textHi;

// One ring segment. `label` may carry newlines for multi-line glyphs (split on '\n').
export interface WheelSegment<K extends string> {
  key: K;
  label: string;
  tier?: BuildTier; // set = skin the slice with the real material texture (build wheel's inner tier ring)
  sub?: string;     // small second line under the label (cost readouts: "100 wood")
  disabled?: boolean; // greyed + non-committable (can't afford / not applicable right now)
}

// One swatch that orbits outside the ring, filled with a real build-tier texture.
export interface WheelSwatch<K extends string> {
  key: K;
  label: string;
  tier: BuildTier; // which tier texture skins the swatch
}

function pol(cx: number, cy: number, r: number, ang: number): [number, number] {
  return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
}

// A generic hold-to-open radial wheel. Keys are opaque strings (piece ids, hammer actions, ...).
// The owner provides segments + optional swatches, an active key for the initial highlight, and
// reacts to commits via onCommit callbacks passed to commitSelection().
export class RadialWheel<SegKey extends string, SwKey extends string> {
  open = false;
  onHide: (() => void) | null = null; // fired when the wheel actually closes (incl. empty-space dismiss); lets an owner relock

  private static nextId = 0;
  private readonly uid = RadialWheel.nextId++; // unique per wheel: keeps each wheel's SVG pattern ids distinct
  private fadeTimer = 0; // token so a re-open cancels a pending fade-out

  protected readonly root: HTMLDivElement;
  protected readonly svg: SVGSVGElement;
  private readonly centerLabel: HTMLDivElement;
  private readonly hubFallbackLabel: string;

  private readonly segs: Array<{ key: SegKey; path: SVGPathElement; text: SVGTextElement; sub: SVGTSpanElement; label: string; tier?: BuildTier; disabled?: boolean }> = [];
  // Optional INNER RING (owner 2026-07-11: the build wheel's material tiers live INSIDE the piece ring).
  // Same key space as the outer ring; movement-mode picking switches ring by the virtual cursor's RADIUS.
  private readonly innerSegs: Array<{ key: SegKey; path: SVGPathElement; text: SVGTextElement; sub: SVGTSpanElement; label: string; tier?: BuildTier; disabled?: boolean }> = [];
  private readonly hubR: number;
  private readonly swatches = new Map<SwKey, { box: SVGRectElement; label: string }>();

  private activeSeg: SegKey | null;
  private activeSwatch: SwKey | null;

  private hoverSeg: SegKey | null = null;
  private hoverSwatch: SwKey | null = null;

  // FIRST-PERSON (pointer-locked) MODE: there is no free cursor to hover a slice with, so the wheel is driven by
  // mouse MOVEMENT instead -- deltas accumulate into a virtual cursor whose ANGLE picks the slice (Rust-style).
  private movementMode = false;
  private cvx = 0; private cvy = 0;                      // virtual-cursor offset from the hub centre
  private moveHandler: ((e: PointerEvent) => void) | null = null;
  private clickHandler: ((e: PointerEvent) => void) | null = null;
  private readonly cursorDot: SVGCircleElement;

  // Apply-and-close, supplied by the subclass. Hovering is done with NATIVE pointer events on each
  // segment/swatch element (no coordinate math, no mousemove reliance), so it just works wherever the
  // cursor is free; the owner frees the pointer lock while the wheel is up (see main.ts).
  private readonly onPointerCommit: () => void;

  constructor(opts: {
    segments: Array<WheelSegment<SegKey>>;
    innerSegments?: Array<WheelSegment<SegKey>>; // optional second ring between hub and the outer ring
    swatches?: Array<WheelSwatch<SwKey>>;
    activeSeg?: SegKey | null;
    activeSwatch?: SwKey | null;
    hubFallbackLabel?: string;
    onPointerCommit: () => void; // called on overlay pointerdown to apply + close
  }) {
    this.activeSeg = opts.activeSeg ?? null;
    this.activeSwatch = opts.activeSwatch ?? null;
    this.hubFallbackLabel = opts.hubFallbackLabel ?? 'BUILD';
    this.onPointerCommit = opts.onPointerCommit;

    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:18', 'display:none',
      'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`, `font-family:${SK.font}`,
    ].join(';');

    const stage = document.createElement('div');
    stage.style.cssText = 'position:relative;width:520px;height:520px;';

    this.svg = document.createElementNS(SVG_NS, 'svg');
    this.svg.setAttribute('viewBox', `0 0 ${VIEW} ${VIEW}`);
    this.svg.setAttribute('width', '520');
    this.svg.setAttribute('height', '520');
    this.svg.style.cssText = 'display:block;filter:drop-shadow(0 18px 50px rgba(0,0,0,0.5));';
    stage.appendChild(this.svg);

    this.hubR = opts.innerSegments?.length ? R_HUB_SMALL : R_INNER;
    this.buildRing(opts.segments, R_INNER, R_OUTER, 13, this.segs);
    if (opts.innerSegments?.length) this.buildRing(opts.innerSegments, this.hubR, R_INNER - 2, 11, this.innerSegs);
    if (opts.swatches) this.buildSwatches(opts.swatches);

    // Hub disc + centered label (HTML on top of the SVG so multi-line text wraps cleanly).
    const hub = document.createElementNS(SVG_NS, 'circle');
    hub.setAttribute('cx', String(CENTER));
    hub.setAttribute('cy', String(CENTER));
    hub.setAttribute('r', String(this.hubR));
    hub.setAttribute('fill', SK.bg1);
    hub.setAttribute('stroke', EDGE_STROKE);
    hub.setAttribute('stroke-width', '2');
    hub.addEventListener('pointerenter', () => this.setHover(null, null)); // back to the hub: clear hover
    this.svg.appendChild(hub);

    // The first-person virtual cursor: a small dot you steer with mouse MOVEMENT. Drawn on top, hidden until
    // the wheel opens while the pointer is locked (see show()).
    this.cursorDot = document.createElementNS(SVG_NS, 'circle');
    this.cursorDot.setAttribute('r', '10');
    this.cursorDot.setAttribute('fill', SK.textHi);
    this.cursorDot.setAttribute('stroke', SK.bg0);
    this.cursorDot.setAttribute('stroke-width', '2');
    this.cursorDot.style.pointerEvents = 'none';
    this.cursorDot.style.display = 'none';
    this.svg.appendChild(this.cursorDot);

    this.centerLabel = document.createElement('div');
    this.centerLabel.style.cssText = [
      'position:absolute', 'left:50%', 'top:50%', 'transform:translate(-50%,-50%)',
      `width:${this.hubR * 2 - 16}px`, 'text-align:center', 'pointer-events:none',
      `color:${SK.textHi}`, 'text-transform:uppercase', 'font-size:14px', 'line-height:1.35', 'letter-spacing:1px', 'white-space:pre-line',
    ].join(';');
    stage.appendChild(this.centerLabel);

    this.root.appendChild(stage);
    document.body.appendChild(this.root);

    // Click on empty overlay space (not a segment/swatch -- those stop propagation and commit themselves):
    // dismiss without selecting. In MOVEMENT MODE there is no cursor to click a slice with, so a click COMMITS
    // whatever the virtual cursor is currently pointing at (and just dismisses if it's resting on the hub).
    this.root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.movementMode && this.hasHover) this.onPointerCommit();
      else this.hide();
    });
  }

  // ---- construction -------------------------------------------------------

  private buildRing(segments: Array<WheelSegment<SegKey>>, rIn: number, rOut: number, fontSize: number, into: Array<{ key: SegKey; path: SVGPathElement; text: SVGTextElement; sub: SVGTSpanElement; label: string; tier?: BuildTier; disabled?: boolean }>): void {
    const n = segments.length;
    const step = (Math.PI * 2) / n;
    const rLabel = (rIn + rOut) / 2;
    // Start the first segment centered at the top (12 o'clock) and run clockwise.
    const start = -Math.PI / 2 - step / 2;
    segments.forEach((p, i) => {
      const a0 = start + i * step;
      const a1 = a0 + step;
      const mid = a0 + step / 2;

      let d: string;
      if (n === 1) {
        // A lone segment spans the whole circle. A single SVG arc can't draw 360deg (start == end, so
        // it degenerates and nothing renders -- which left nothing to hover/click). Draw a real donut
        // instead: outer + inner circles as paired semicircle arcs, even-odd filled so the ring
        // between them is solid and hittable (this is the hammer's Remove option).
        const ring = (r: number) =>
          `M ${CENTER + r} ${CENTER} A ${r} ${r} 0 1 1 ${CENTER - r} ${CENTER} A ${r} ${r} 0 1 1 ${CENTER + r} ${CENTER} Z`;
        d = `${ring(rOut)} ${ring(rIn)}`;
      } else {
        const [x0o, y0o] = pol(CENTER, CENTER, rOut, a0);
        const [x1o, y1o] = pol(CENTER, CENTER, rOut, a1);
        const [x0i, y0i] = pol(CENTER, CENTER, rIn, a0);
        const [x1i, y1i] = pol(CENTER, CENTER, rIn, a1);
        d = [
          `M ${x0o} ${y0o}`,
          `A ${rOut} ${rOut} 0 0 1 ${x1o} ${y1o}`,
          `L ${x1i} ${y1i}`,
          `A ${rIn} ${rIn} 0 0 0 ${x0i} ${y0i}`,
          'Z',
        ].join(' ');
      }

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      if (n === 1) path.setAttribute('fill-rule', 'evenodd'); // keep the donut hole open
      path.setAttribute('fill', p.tier ? this.swatchPattern(p.tier) : this.isSegActive(p.key) ? SEG_FILL_ACTIVE : SEG_FILL);
      if (p.tier) path.setAttribute('fill-opacity', this.isSegActive(p.key) ? '1' : '0.55'); // textured tier slice: selection brightens the material itself
      path.setAttribute('stroke', SEG_STROKE);
      path.setAttribute('stroke-width', '1.5');
      path.style.transition = 'fill 90ms linear';
      path.style.cursor = 'pointer';
      // Native hover/click on the slice itself: highlight on enter, select on click. Release-to-select
      // (let go of the wheel bind) reads whatever this last set as the hover.
      path.addEventListener('pointerenter', () => this.setHover(p.key, null));
      path.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); this.setHover(p.key, null); this.onPointerCommit(); });
      this.svg.appendChild(path);

      const [lx, ly] = pol(CENTER, CENTER, rLabel, mid);
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('x', String(lx));
      text.setAttribute('y', String(ly));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('fill', TEXT_IDLE);
      text.setAttribute('font-size', String(fontSize));
      text.setAttribute('font-family', SK.font);
      text.style.pointerEvents = 'none';
      // Multi-line labels via tspans, centered on the radial midpoint.
      const lines = p.label.split('\n');
      lines.forEach((line, li) => {
        const tspan = document.createElementNS(SVG_NS, 'tspan');
        tspan.setAttribute('x', String(lx));
        tspan.setAttribute('dy', li === 0 ? `${-(lines.length - 1) * 0.55}em` : '1.1em');
        tspan.textContent = line;
        text.appendChild(tspan);
      });
      // Small SUB line (cost readouts). Always appended so setSegState can fill it live; empty = invisible.
      const sub = document.createElementNS(SVG_NS, 'tspan');
      sub.setAttribute('x', String(lx));
      sub.setAttribute('dy', '1.2em');
      sub.setAttribute('font-size', String(Math.max(9, fontSize - 3)));
      sub.setAttribute('fill', SK.dim); // costs read dim by default; paint() runs them red when short
      sub.textContent = p.sub ?? '';
      text.appendChild(sub);
      this.svg.appendChild(text);

      into.push({ key: p.key, path, text, sub, tier: p.tier, label: p.label.replace(/\n/g, ' '), disabled: p.disabled });
    });
  }

  // Live per-segment state (hammer/build wheels refresh costs + affordability every open). Disabled
  // segments grey out and will not commit -- hover still shows the label so the WHY is readable.
  setSegState(key: SegKey, state: { sub?: string; disabled?: boolean }): void {
    const s = [...this.segs, ...this.innerSegs].find((x) => x.key === key);
    if (!s) return;
    if (state.sub !== undefined) s.sub.textContent = state.sub;
    if (state.disabled !== undefined) s.disabled = state.disabled;
    this.paint();
  }

  private buildSwatches(swatches: Array<WheelSwatch<SwKey>>): void {
    const n = swatches.length;
    // Spread the swatches across the bottom arc, evenly spaced, laid out LEFT-TO-RIGHT in array order
    // (so twig/wood/stone read left to right, matching the natural upgrade progression on both wheels).
    const spread = Math.PI * 0.5;
    const base = Math.PI / 2; // 6 o'clock
    swatches.forEach((sw, i) => {
      const ang = base + spread / 2 - (n === 1 ? spread / 2 : (spread * i) / (n - 1));
      const [cx, cy] = pol(CENTER, CENTER, R_TIER, ang);

      const group = document.createElementNS(SVG_NS, 'g');

      const box = document.createElementNS(SVG_NS, 'rect');
      box.setAttribute('x', String(cx - TIER_SW / 2));
      box.setAttribute('y', String(cy - TIER_SW / 2));
      box.setAttribute('width', String(TIER_SW));
      box.setAttribute('height', String(TIER_SW));
      box.setAttribute('rx', '9');
      box.setAttribute('fill', this.swatchPattern(sw.tier));
      box.setAttribute('stroke', sw.key === this.activeSwatch ? SEG_STROKE_ACTIVE : EDGE_STROKE);
      box.setAttribute('stroke-width', sw.key === this.activeSwatch ? '3' : '1.5');
      box.style.transition = 'stroke 90ms linear';
      box.style.cursor = 'pointer';
      box.addEventListener('pointerenter', () => this.setHover(null, sw.key));
      box.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); this.setHover(null, sw.key); this.onPointerCommit(); });
      group.appendChild(box);

      const cap = document.createElementNS(SVG_NS, 'text');
      cap.setAttribute('x', String(cx));
      cap.setAttribute('y', String(cy + TIER_SW / 2 + 14));
      cap.setAttribute('text-anchor', 'middle');
      cap.setAttribute('fill', TEXT_IDLE);
      cap.setAttribute('font-size', '12');
      cap.setAttribute('font-family', SK.font);
      cap.setAttribute('letter-spacing', '1');
      cap.style.pointerEvents = 'none';
      cap.textContent = sw.label;
      group.appendChild(cap);

      this.svg.appendChild(group);
      this.swatches.set(sw.key, { box, label: sw.label });
    });
  }

  // Turn the real tier CanvasTexture into an SVG pattern fill so swatches show the actual material.
  // Falls back to a flat color if the canvas isn't available (e.g. headless tests).
  private swatchPattern(t: BuildTier): string {
    const fallback: Record<BuildTier, string> = { twig: '#6e5e3f', wood: '#5a3c21', stone: '#6d6d6d', metal: '#7d8188', reinforced: '#98a0ab' };
    let defs = this.svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS(SVG_NS, 'defs');
      this.svg.insertBefore(defs, this.svg.firstChild);
    }
    // ID must be unique PER WHEEL: each wheel has its own <svg>, and a url(#id) reference resolves to
    // the first matching id in the whole document. With a shared id the hammer wheel's swatches pointed
    // at the build wheel's patterns (a different svg) and rendered empty. The per-wheel uid fixes that.
    const id = `tierpat_${this.uid}_${t}`;
    if (this.svg.querySelector(`#${id}`)) return `url(#${id})`; // already minted in this wheel's svg
    const src = getTierMaterial(t).map?.image as CanvasImageSource | undefined;
    let url: string | null = null;
    try {
      if (src instanceof HTMLCanvasElement) url = src.toDataURL('image/png');
    } catch {
      url = null;
    }
    if (!url) return fallback[t];

    // TILING pattern in user space (owner fix): objectBoundingBox placed ONE small image in each shape's
    // bounding box, so the wide inner-ring arc slices showed a stretched, patchy crop. A fixed-size tile in
    // user space repeats seamlessly across every slice at the same density -- the material fits each zone.
    const TILE = 72;
    const pattern = document.createElementNS(SVG_NS, 'pattern');
    pattern.setAttribute('id', id);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', String(TILE));
    pattern.setAttribute('height', String(TILE));
    const img = document.createElementNS(SVG_NS, 'image');
    img.setAttribute('href', url);
    img.setAttribute('x', '0');
    img.setAttribute('y', '0');
    img.setAttribute('width', String(TILE));
    img.setAttribute('height', String(TILE));
    img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    pattern.appendChild(img);
    defs.appendChild(pattern);
    return `url(#${id})`;
  }

  // ---- hover / selection --------------------------------------------------

  // Set the hovered segment/swatch (driven by the elements' own pointerenter handlers) and repaint.
  private setHover(seg: SegKey | null, swatch: SwKey | null): void {
    if (seg === this.hoverSeg && swatch === this.hoverSwatch) return;
    this.hoverSeg = seg;
    this.hoverSwatch = swatch;
    this.paint();
  }

  // Whether a segment should render as the current selection. The base tracks ONE active segment; a
  // subclass whose ring mixes two selection kinds (BuildWheel: a piece AND a tier) overrides this so
  // both stay highlighted between opens.
  protected isSegActive(key: SegKey): boolean { return key === this.activeSeg; }

  protected repaint(): void { this.paint(); } // let subclasses refresh after changing their own active state

  private paint(): void {
    for (const s of [...this.segs, ...this.innerSegs]) {
      const hovered = s.key === this.hoverSeg;
      const active = this.isSegActive(s.key);
      s.sub.setAttribute('fill', s.disabled ? SK.bad : SK.dim); // costs: dim, red only when out of reach
      if (s.disabled) {
        // Locked: the slice drops to ~60% alpha with a faint label (can't afford / not applicable).
        // Hover still lifts the text a touch so the sub line (the WHY: its cost) stays readable;
        // commit() skips it regardless.
        s.path.setAttribute('fill', SEG_FILL);
        s.path.setAttribute('fill-opacity', '0.68');
        s.path.setAttribute('stroke', SEG_STROKE);
        s.path.setAttribute('stroke-width', '1.5');
        s.text.setAttribute('fill', hovered ? SK.dim : SK.faint);
        continue;
      }
      if (s.tier) {
        // Textured tier slice: the material pattern IS the fill, so hover/active read via opacity + border.
        s.path.setAttribute('fill', this.swatchPattern(s.tier));
        s.path.setAttribute('fill-opacity', hovered ? '1' : active ? '0.95' : '0.55');
        s.path.setAttribute('stroke', hovered ? RED_STROKE : active ? SEG_STROKE_ACTIVE : SEG_STROKE);
        s.path.setAttribute('stroke-width', hovered ? '3' : active ? '2.5' : '1.5');
        s.text.setAttribute('fill', hovered || active ? TEXT_HOVER : TEXT_IDLE);
        continue;
      }
      // Hover and the SELECTED (active) slice share the accent language -- accent-soft fill, accent
      // ring, lifted label -- so you can always see what is currently chosen between opens.
      s.path.setAttribute('fill', hovered ? SEG_FILL_HOVER : active ? SEG_FILL_ACTIVE : SEG_FILL);
      s.path.setAttribute('fill-opacity', '1'); // clear a locked slice's knock-down once it re-enables
      s.path.setAttribute('stroke', hovered || active ? SEG_STROKE_ACTIVE : SEG_STROKE);
      s.path.setAttribute('stroke-width', hovered || active ? '2.5' : '1.5');
      s.text.setAttribute('fill', hovered || active ? TEXT_HOVER : TEXT_IDLE);
    }
    for (const [k, sw] of this.swatches) {
      const hovered = k === this.hoverSwatch;
      const active = k === this.activeSwatch;
      sw.box.setAttribute('stroke', hovered ? RED_STROKE : active ? SEG_STROKE_ACTIVE : EDGE_STROKE);
      sw.box.setAttribute('stroke-width', hovered || active ? '3' : '1.5');
    }

    // Center label: prefer the hovered swatch/segment, else the current active segment.
    if (this.hoverSwatch) {
      this.centerLabel.textContent = this.hubLabelForSwatch(this.hoverSwatch, this.swatches.get(this.hoverSwatch)!.label);
    } else if (this.hoverSeg) {
      this.centerLabel.textContent = [...this.segs, ...this.innerSegs].find((s) => s.key === this.hoverSeg)?.label ?? '';
    } else {
      this.centerLabel.textContent = this.segs.find((s) => s.key === this.activeSeg)?.label ?? this.hubFallbackLabel;
    }
  }

  // What the hub shows while a swatch is hovered. Default appends "tier"; subclasses may override.
  protected hubLabelForSwatch(_key: SwKey, label: string): string {
    return `${label}\ntier`;
  }

  // ---- protected accessors for subclasses ---------------------------------

  // Apply whatever the cursor is hovering. Subclasses supply what to do for the hovered swatch
  // (e.g. select a tier) and the hovered segment (e.g. select a piece / a hammer action). A swatch
  // always becomes the active swatch; a segment becomes the active (sticky) segment unless
  // stickySeg is false (one-shot actions like Remove should not stay highlighted).
  protected commit(handlers: {
    onSwatch?: (key: SwKey, changed: boolean) => void;
    onSeg?: (key: SegKey) => void;
    stickySeg?: boolean;
  }): void {
    if (this.hoverSwatch && handlers.onSwatch) {
      const changed = this.hoverSwatch !== this.activeSwatch;
      this.activeSwatch = this.hoverSwatch;
      handlers.onSwatch(this.hoverSwatch, changed);
    }
    if (this.hoverSeg && handlers.onSeg) {
      // A disabled slice never commits (greyed: can't afford / not applicable) -- the wheel just closes.
      const seg = [...this.segs, ...this.innerSegs].find((s) => s.key === this.hoverSeg);
      if (seg?.disabled) return;
      if (handlers.stickySeg !== false) this.activeSeg = this.hoverSeg;
      handlers.onSeg(this.hoverSeg);
    }
  }

  // ---- visibility ---------------------------------------------------------

  // True when the cursor is currently over a segment or swatch (something would commit on release).
  get hasHover(): boolean {
    return this.hoverSeg !== null || this.hoverSwatch !== null;
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    this.hoverSeg = null;
    this.hoverSwatch = null;
    this.fadeTimer++; // cancel any in-flight fade-out
    this.root.style.transition = '';
    this.root.style.opacity = '1';
    this.root.style.display = 'flex';
    // FIRST PERSON: the pointer is locked (no free cursor), so drive the wheel by mouse MOVEMENT -- a virtual
    // cursor you steer with deltas, its angle picking the slice. THIRD PERSON: the pointer is free, keep the
    // native hover-a-slice behaviour. Detected once, here, from the live lock state.
    this.movementMode = document.pointerLockElement != null;
    // Movement mode: the OS pointer is frozen, so a click could land on the wrong slice via its native handler.
    // Turn OFF the SVG's pointer events so slices never hover/commit themselves -- the virtual cursor drives the
    // hover and the root-overlay click (below) commits whatever it points at.
    this.svg.style.pointerEvents = this.movementMode ? 'none' : '';
    if (this.movementMode) {
      this.cvx = 0; this.cvy = 0;
      this.cursorDot.setAttribute('cx', String(CENTER));
      this.cursorDot.setAttribute('cy', String(CENTER));
      this.cursorDot.style.display = '';
      this.moveHandler = (e) => this.onVirtualMove(e);
      window.addEventListener('pointermove', this.moveHandler);
      // In pointer lock the OS routes clicks to the locked CANVAS, not this overlay, so the root pointerdown that
      // commits in mouse mode NEVER fires -- that is why the base-claim + keypad wheels could not be selected in
      // first person. Listen on WINDOW (capture) so a LEFT-click commits the virtual cursor's slice, and consume
      // it (stopImmediatePropagation) so the same click never also fires a shot/build.
      this.clickHandler = (e) => { if (e.button !== 0) return; e.preventDefault(); e.stopImmediatePropagation(); if (this.hasHover) this.onPointerCommit(); else this.hide(); };
      window.addEventListener('pointerdown', this.clickHandler, true);
    } else {
      this.cursorDot.style.display = 'none';
    }
    this.paint();
  }

  // Movement mode: accumulate the raw mouse deltas into the virtual cursor, clamp it inside the ring, and pick
  // the slice its ANGLE falls in (nothing while it rests inside the hub deadzone).
  private onVirtualMove(e: PointerEvent): void {
    const SENS = 1.15; // a small gain so a short flick reaches the ring
    this.cvx += (e.movementX || 0) * SENS;
    this.cvy += (e.movementY || 0) * SENS;
    let mag = Math.hypot(this.cvx, this.cvy);
    const maxR = R_OUTER - 8;
    if (mag > maxR) { this.cvx *= maxR / mag; this.cvy *= maxR / mag; mag = maxR; }
    this.cursorDot.setAttribute('cx', String(CENTER + this.cvx));
    this.cursorDot.setAttribute('cy', String(CENTER + this.cvy));
    if (mag < this.hubR) { this.setHover(null, null); return; } // resting on the hub: no slice
    const ang = Math.atan2(this.cvy, this.cvx);
    // Inside the outer ring's inner edge = the INNER ring (when one exists), else nothing yet.
    if (mag < R_INNER && this.innerSegs.length) { this.setHover(this.ringAtAngle(this.innerSegs, ang), null); return; }
    this.setHover(this.ringAtAngle(this.segs, ang), null);
  }

  // Which slice the given screen-space angle falls in. Mirrors buildSegments' layout: the first slice is centred
  // at 12 o'clock and they run clockwise (start = -PI/2 - step/2).
  private ringAtAngle(ring: Array<{ key: SegKey }>, ang: number): SegKey | null {
    const n = ring.length;
    if (n === 0) return null;
    if (n === 1) return ring[0].key;
    const step = (Math.PI * 2) / n;
    const start = -Math.PI / 2 - step / 2;
    const rel = (((ang - start) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return ring[Math.floor(rel / step) % n].key;
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    if (this.moveHandler) { window.removeEventListener('pointermove', this.moveHandler); this.moveHandler = null; }
    if (this.clickHandler) { window.removeEventListener('pointerdown', this.clickHandler, true); this.clickHandler = null; }
    this.movementMode = false;
    this.cursorDot.style.display = 'none';
    // Quick fade-out so a click/release selection visibly registers before the wheel vanishes (instead
    // of snapping away). A re-open bumps fadeTimer so a stale timeout never hides the freshly-shown wheel.
    const tok = ++this.fadeTimer;
    this.root.style.transition = 'opacity 110ms ease-out';
    this.root.style.opacity = '0';
    window.setTimeout(() => { if (tok === this.fadeTimer && !this.open) this.root.style.display = 'none'; }, 140);
    this.onHide?.();
  }

  // Back-compat helper. The wheel is hold-to-open, so a plain toggle just flips visibility
  // (release-to-select callers should use show()/commitSelection()/hide()).
  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }
}
